// Ruleset identity and store-group shaping for the TPR Pro rules page.
//
// Rules are soft-deleted (disabled), so a party can hold a disabled ruleset alongside a new one that
// looks the same. Everything that creates, enables or groups rulesets goes through the identity here
// so that no party ever ends up with two enabled copies of the same rule.
//
// This lives outside TprPro.vue so the grouping and create routing can be tested on plain arrays.

import { buildCompetitiveRuleEntry, competitiveSignatureFields, isCompetitiveRuleName } from '@/utils/competitiveRules'

// The same rule regardless of its enabled state. Built from the GET /rulesets row shape.
export function rulesetIdentity(ruleset) {
  const brackets = Array.isArray(ruleset.brackets)
    ? ruleset.brackets.map(b => `${b.min}|${b.max}|${b.units}|${b.price}`).sort()
    : []
  // competitiveSignatureFields owns value + competitor/margin/rounding/item_group; non-competitive get null/false defaults.
  return JSON.stringify({
    name: ruleset.rule_name,
    ...competitiveSignatureFields(ruleset),
    brackets,
    filter_lvl: ruleset.filter_lvl || '',
    filter_value: ruleset.filter_value || '',
    perishable: ruleset.perishable ?? null
  })
}

// Identity plus enabled state; rows only group together in the store-group view when both match.
export function rulesetSignature(ruleset) {
  return JSON.stringify([rulesetIdentity(ruleset), ruleset.enabled !== false])
}

// The rule_list entry PUT/POST /rulesets expects, from a GET /rulesets row.
export function buildRuleEntry(rule) {
  const isBracket = rule.label === 'Bracket'
  const isCompetitive = isCompetitiveRuleName(rule.rule_name)
  const ruleEntry = {
    display_name: rule.rule_display_name,
    name: rule.rule_name,
    value: isBracket || isCompetitive ? null : Number(rule.rule_value)
  }
  if (isBracket) ruleEntry.brackets = rule.brackets || []
  if (isCompetitive) Object.assign(ruleEntry, buildCompetitiveRuleEntry(rule))
  return ruleEntry
}

// A GET-shaped row for a rule that is about to be saved, so it can be compared by identity.
export function rulesetFromEntry(ruleEntry, { filter_lvl, filter_value, perishable, item_group }) {
  return {
    rule_name: ruleEntry.name,
    rule_value: ruleEntry.value ?? null,
    brackets: ruleEntry.brackets,
    competitor: ruleEntry.competitor,
    direction: ruleEntry.direction,
    margin_threshold: ruleEntry.margin_threshold,
    apply_rounding: ruleEntry.apply_rounding,
    item_group: item_group ?? null,
    filter_lvl: filter_lvl || '',
    filter_value: filter_value || '',
    perishable: perishable ?? null
  }
}

// Rulesets on a party with the same identity, optionally narrowed by enabled state or excluding one ruleset.
export function findTwins(partyRulesets, identity, { enabled, excludeId } = {}) {
  return (partyRulesets || []).filter(ruleset =>
    ruleset.ruleset_id !== excludeId &&
    (enabled === undefined || (ruleset.enabled !== false) === enabled) &&
    rulesetIdentity(ruleset) === identity
  )
}

// Decides, per target, whether saving a rule creates it, re-enables a disabled copy, or is skipped.
//   enabled rule:  an enabled twin -> skip; a disabled twin -> re-enable it; otherwise create
//   disabled rule: any twin -> skip; otherwise create
// `targets` are { party_id, identity, ... } objects (one save can span several filter values, each its
// own identity); `rulesetsByParty` maps party_id -> that party's GET rows.
export function planRulesetSaves(targets, enabled, rulesetsByParty) {
  const plan = { create: [], reenable: [], skip: [] }
  targets.forEach(target => {
    const twins = findTwins(rulesetsByParty[target.party_id], target.identity)
    if (!twins.length) {
      plan.create.push(target)
    } else if (enabled && !twins.some(twin => twin.enabled !== false)) {
      plan.reenable.push({ target, existing: twins[0] })
    } else {
      plan.skip.push(target)
    }
  })
  return plan
}

// e.g. "Created 2 rules, re-enabled 1 previously disabled rule, skipped 1 that already exists."
export function describeSavePlan(plan) {
  const count = (n, one, many) => `${n} ${n === 1 ? one : many}`
  const parts = []
  if (plan.create.length) parts.push(`created ${count(plan.create.length, 'rule', 'rules')}`)
  if (plan.reenable.length) parts.push(`re-enabled ${count(plan.reenable.length, 'previously disabled rule', 'previously disabled rules')}`)
  if (plan.skip.length) parts.push(`skipped ${count(plan.skip.length, 'that already exists', 'that already exist')}`)
  const text = parts.join(', ')
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`
}

// Keep rules shared by every store (with group_targets for fan-out); partial rules become deviations.
// Disabled rulesets only take part when includeDisabled is set, so a rule disabled on some stores
// doesn't show up as drift while disabled rules are hidden.
export function intersectStoreRulesets(perStoreRulesets, children, { includeDisabled = false } = {}) {
  const bySignature = new Map()
  perStoreRulesets.forEach((rulesets, index) => {
    const child = children[index]
    const seen = new Set()
    ;(rulesets || []).forEach(ruleset => {
      if (!includeDisabled && ruleset.enabled === false) return
      const signature = rulesetSignature(ruleset)
      if (seen.has(signature)) return
      seen.add(signature)
      if (!bySignature.has(signature)) bySignature.set(signature, { rep: ruleset, targets: [] })
      bySignature.get(signature).targets.push({
        ruleset_id: ruleset.ruleset_id,
        party_id: child.store_party_id,
        name: child.store_name
      })
    })
  })

  const common = []
  const deviations = []
  bySignature.forEach(entry => {
    if (entry.targets.length === children.length) {
      common.push({ ...entry.rep, group_targets: entry.targets })
    } else {
      const presentIds = new Set(entry.targets.map(target => target.party_id))
      const missingChildren = children.filter(child => !presentIds.has(child.store_party_id))
      deviations.push({
        rep: entry.rep,
        present: entry.targets.map(target => target.name),
        missing: missingChildren.map(child => child.store_name),
        presentTargets: entry.targets,
        missingTargets: missingChildren.map(child => ({ party_id: child.store_party_id, name: child.store_name }))
      })
    }
  })
  return { common, deviations }
}
